import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import { CheckboxSelect, type ValueType } from '@atlaskit/select';
import { layers } from '@atlaskit/theme';
import ListWithPopup from '@atlassian/list-with-popup/ListWithPopup.tsx';
import { TagItem, TagItemPopupContentWrapper } from '@atlassian/list-with-popup/TagItem.tsx';

import type { FieldBaseProps } from '../common/types';

import type {
	JiraCheckboxesField_Platform$data,
	JiraCheckboxesField_Platform$key,
} from './__generated__/JiraCheckboxesField_Platform.graphql';
import { JiraInlineField, type JiraInlineFieldProps } from './JiraInlineField';

const FIELD_TYPE_FOR_ANALYTICS = 'JiraCheckboxesField';

const messages = defineMessages({
	none: {
		id: 'jira.jira.jira-platform-fields.jira-checkboxes-field.none',
		defaultMessage: 'None',
		description: 'Display text shown when no value has been set for the field',
	},
	showMoreTooltip: {
		id: 'jira.jira.jira-platform-fields.jira-checkboxes-field.show-more-tooltip',
		defaultMessage: 'Show more options',
		description: 'Tooltip for the show more options button',
	},
});

type Option = { label: string; value: string };
type FieldValue = ValueType<Option, true>;

const parseOptions = (fieldOptions: JiraCheckboxesField_Platform$data['fieldOptions']) => {
	const options = [];

	if (fieldOptions?.edges) {
		for (const option of fieldOptions.edges) {
			const value = option?.node?.value;
			if (value != null) {
				options.push({ value, label: value });
			}
		}
	}

	return options;
};

const ReadView = memo(({ selectedOptions }: { selectedOptions: readonly Option[] }) => {
	const { formatMessage } = useIntl();

	return (
		<ListWithPopup
			items={selectedOptions.map((opt) => ({
				key: opt.value,
				name: opt.label,
				isColored: false,
			}))}
			ItemComponent={TagItem}
			PopupContentWrapper={TagItemPopupContentWrapper}
			showMoreTooltip={formatMessage(messages.showMoreTooltip)}
			isHoverPopoverEnabled
		/>
	);
});

const readView: JiraInlineFieldProps<FieldValue>['readView'] = (readViewProps) => (
	<ReadView selectedOptions={readViewProps.value} />
);

export type JiraCheckboxesFieldProps = FieldBaseProps & {
	field: JiraCheckboxesField_Platform$key;
	onChange: (newValue: string[] | null) => void;
};

/**
 * `JiraCheckboxesField` is a React component that renders a Jira checkboxes field,
 * supporting both editable and read-only modes. It displays the field value and allows
 * users to select multiple options from checkboxes if the field is editable.
 *
 * @param field - The Relay fragment reference for the Jira checkboxes field.
 * @param fieldValue - The current selected values (array of strings).
 * @param onChange - Callback invoked when the selected values change.
 */
export const JiraCheckboxesField: React.MemoExoticComponent<
	({ fieldValue, field, isFieldEditable, onChange }: JiraCheckboxesFieldProps) => JSX.Element
> = memo(
	({
		fieldValue,
		field,
		isFieldEditable = true,
		onChange,
	}: JiraCheckboxesFieldProps): JSX.Element => {
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
		const data = useFragment<JiraCheckboxesField_Platform$key>(
			graphql`
				fragment JiraCheckboxesField_Platform on JiraCheckboxesField {
					name
					fieldConfig {
						isRequired
						isEditable
					}
					selectedOptions {
						edges {
							node {
								value
							}
						}
					}
					fieldOptions {
						edges {
							node {
								value
							}
						}
					}
				}
			`,
			// eslint-disable-next-line @atlassian/relay/use-fragment-naming
			field,
		);

		const { formatMessage } = useIntl();

		const isEditable = !!data?.fieldConfig?.isEditable && isFieldEditable;
		const isRequired = !!data?.fieldConfig?.isRequired;
		const fieldName = data.name;

		const defaultValues = useMemo(() => {
			const values: string[] = [];
			if (data.selectedOptions?.edges) {
				for (const edge of data.selectedOptions.edges) {
					const value = edge?.node?.value;
					if (value != null) {
						values.push(value);
					}
				}
			}
			return values.length > 0 ? values : null;
		}, [data.selectedOptions]);

		const currentValue = useMemo(() => {
			if (Array.isArray(fieldValue)) {
				return fieldValue.filter((v): v is string => typeof v === 'string');
			}
			return null;
		}, [fieldValue]);

		const hasValue = currentValue != null && currentValue.length > 0;

		const options = parseOptions(data.fieldOptions);
		const selectedOptions: Option[] = useMemo(
			() => (hasValue ? currentValue.map((val) => ({ value: val, label: val })) : []),
			[hasValue, currentValue],
		);

		// Display text for editButtonLabel
		const editButtonLabel = useMemo(() => {
			if (!hasValue) {
				return formatMessage(messages.none);
			}
			if (currentValue.length === 1) {
				return currentValue[0];
			}
			return currentValue.join(', ');
		}, [hasValue, currentValue, formatMessage]);

		// Allows us to make sure we only fall back to default value once, on initial render
		const isFirstRenderRef = useRef(true);
		useEffect(() => {
			// If the passed down value is empty and default values are available, tell the upstream
			if (!fieldValue && defaultValues != null && isFirstRenderRef.current) {
				onChange(defaultValues);
			}
			isFirstRenderRef.current = false;
		}, [fieldValue, defaultValues, onChange]);

		const onConfirm = useCallback(
			(newValue: ValueType<Option, true>) => {
				// Extract string values from options
				const newValues =
					newValue && newValue.length > 0
						? newValue.map((opt) => String(opt.value)).filter(Boolean)
						: null;

				// Compare arrays for changes
				const hasChanged = (() => {
					if (!newValues && !currentValue) {
						return false;
					}
					if (!newValues || !currentValue) {
						return true;
					}
					if (newValues.length !== currentValue.length) {
						return true;
					}

					const currentSet = new Set(currentValue);
					return !newValues.every((val) => currentSet.has(val));
				})();

				if (hasChanged) {
					onChange(newValues);
				}
			},
			[currentValue, onChange],
		);

		const editView: JiraInlineFieldProps<FieldValue>['editView'] = useCallback(
			(fieldProps) => (
				<CheckboxSelect
					{...fieldProps}
					label={fieldName}
					options={options}
					autoFocus
					isRequired={isRequired}
					isClearable
					menuPosition={fg('gravityai-4581-jira-aifc-iframe-korosu') ? 'fixed' : 'absolute'}
					menuPlacement={fg('gravityai-4581-jira-aifc-iframe-korosu') ? 'auto' : 'bottom'}
					styles={
						fg('gravityai-4581-jira-aifc-iframe-korosu')
							? {
									control: (base) => ({
										...base,
										height: '40px',
										minHeight: '40px',
										maxHeight: '40px',
										boxSizing: 'border-box',
										flexWrap: 'nowrap',
										overflow: 'hidden',
									}),
									valueContainer: (base) => ({
										...base,
										flexWrap: 'nowrap',
										overflow: 'hidden',
									}),
									menu: (base) => ({ ...base, zIndex: layers.modal() }),
									menuPortal: (base) => ({ ...base, zIndex: layers.modal() }),
								}
							: undefined
					}
				/>
			),
			[fieldName, isRequired, options],
		);

		return (
			<JiraInlineField<FieldValue>
				fieldName={fieldName}
				fieldTypeForAnalytics={FIELD_TYPE_FOR_ANALYTICS}
				isEditable={isEditable}
				isRequired={isRequired}
				readView={readView}
				editView={editView}
				defaultValue={selectedOptions}
				editButtonLabel={editButtonLabel}
				onConfirm={onConfirm}
			/>
		);
	},
);
